import { getFirstValue } from '../Account/profileFormUtils';
import { formatDisplayDate } from '../../utils/dateFormat';

/**
 * @typedef {{
 *   id: number,
 *   code: string,
 *   discountType: number,
 *   value: number,
 *   maxAmount: number,
 *   description: string,
 *   endDate: string,
 *   startDate: string,
 *   isPackagingExempt: boolean,
 *   isDeliveryExempt: boolean,
 *   minOrderValue: number,
 * }} NormalizedCoupon
 */

/**
 * @param {unknown} value
 * @param {number} [fallback]
 */
function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * @param {unknown} raw
 * @returns {NormalizedCoupon | null}
 */
export function normalizeCoupon(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const code = String(getFirstValue(/** @type {Record<string, unknown>} */ (raw), ['code']) || '').trim();
  if (!code) return null;

  const obj = /** @type {Record<string, unknown>} */ (raw);

  return {
    id: toNumber(getFirstValue(obj, ['id']), 0),
    code,
    discountType: toNumber(getFirstValue(obj, ['discount_type', 'discountType']), 1),
    value: toNumber(getFirstValue(obj, ['value']), 0),
    maxAmount: toNumber(getFirstValue(obj, ['max_amount', 'maxAmount']), 0),
    description: String(getFirstValue(obj, ['description']) || ''),
    endDate: String(getFirstValue(obj, ['end_date', 'endDate']) || ''),
    startDate: String(getFirstValue(obj, ['start_date', 'startDate']) || ''),
    isPackagingExempt: Boolean(obj.isPackagingExempt ?? obj.is_packaging_exempt),
    isDeliveryExempt: Boolean(obj.isDeliveryExempt ?? obj.is_delivery_exempt),
    minOrderValue: toNumber(getFirstValue(obj, ['min_order_value', 'minOrderValue']), 0),
  };
}

/**
 * @param {unknown} payload API body or nested `data`
 * @returns {unknown[]}
 */
export function extractCouponsArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const data = /** @type {Record<string, unknown>} */ (payload).data;
    if (Array.isArray(data)) return data;
  }
  return [];
}

/**
 * @param {NormalizedCoupon | null | undefined} coupon
 * @param {number} subtotal
 */
export function isCouponEligible(coupon, subtotal) {
  if (!coupon) return false;
  const min = coupon.minOrderValue ?? 0;
  return subtotal >= min;
}

/**
 * Flutter parity: type 1 = fixed, type 2 = percent (capped by maxAmount).
 * @param {NormalizedCoupon | null | undefined} coupon
 * @param {number} subtotal
 */
export function calculateCouponDiscount(coupon, subtotal) {
  if (!coupon || subtotal <= 0) return 0;

  if (coupon.discountType === 2) {
    let discounted = (coupon.value / 100) * subtotal;
    if (coupon.maxAmount > 0 && discounted > coupon.maxAmount) {
      discounted = coupon.maxAmount;
    }
    return roundMoney(discounted);
  }

  if (coupon.discountType === 1) {
    return roundMoney(Math.min(coupon.value, subtotal));
  }

  return 0;
}

/**
 * @param {NormalizedCoupon} coupon
 */
export function formatCouponOfferLabel(coupon) {
  if (coupon.discountType === 2) {
    const cap =
      coupon.maxAmount > 0 ? ` (up to ₹${coupon.maxAmount.toFixed(0)})` : '';
    return `${coupon.value}% off${cap}`;
  }
  return `₹${coupon.value.toFixed(0)} off`;
}

/**
 * @param {NormalizedCoupon} coupon
 */
export function formatCouponExpiry(coupon) {
  if (!coupon.endDate) return '';
  const formatted = formatDisplayDate(coupon.endDate);
  return formatted || '';
}

/**
 * @param {NormalizedCoupon[]} coupons
 * @param {string} code
 */
export function findCouponByCode(coupons, code) {
  const normalized = String(code || '')
    .trim()
    .toUpperCase();
  if (!normalized) return null;
  return coupons.find((c) => c.code.toUpperCase() === normalized) ?? null;
}

/**
 * @param {number} n
 */
function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Delivery charge after coupon (pickup always 0).
 * @param {{ baseDelivery: number, fulfillmentType: string, appliedCoupon?: NormalizedCoupon | null }} opts
 */
export function resolveDeliveryCharge({ baseDelivery, fulfillmentType, appliedCoupon }) {
  if (fulfillmentType === 'pickup') return 0;
  if (appliedCoupon?.isDeliveryExempt) return 0;
  return baseDelivery;
}

/** Default GST % when checkCart omits it (API returns 5). */
export const CART_DEFAULT_GST_PERCENT = 5;

/** @deprecated Flat delivery — only used if checkCart unavailable. */
export const CART_DELIVERY_FEE = 40;
export const CART_FREE_DELIVERY_THRESHOLD = 499;

/**
 * API parity: round(subTotal * 0.1)
 * @param {number} subtotal
 */
export function estimatePackagingFee(subtotal) {
  if (subtotal <= 0) return 0;
  return Math.round(subtotal * 0.1);
}

/**
 * Flutter parity: (gstPercent / 100) * (subtotal + packaging - discount)
 * @param {number} gstPercent
 * @param {number} subtotal
 * @param {number} packagingFee
 * @param {number} discount
 */
export function calculateGstAmount(gstPercent, subtotal, packagingFee, discount) {
  const base = Math.max(0, subtotal + packagingFee - discount);
  return roundMoney((Number(gstPercent) / 100) * base);
}

/**
 * @param {number} packagingCharges
 * @param {import('./cartCouponUtils').NormalizedCoupon | null | undefined} appliedCoupon
 */
export function resolvePackagingCharge(packagingCharges, appliedCoupon) {
  if (appliedCoupon?.isPackagingExempt) return 0;
  return Math.max(0, Number(packagingCharges) || 0);
}

/**
 * @param {{
 *   packagingCharges: number,
 *   deliveryCharges: number,
 *   gstPercent: number,
 *   cartTotal: number,
 *   discount: number,
 *   fulfillmentType: string,
 *   appliedCoupon?: import('./cartCouponUtils').NormalizedCoupon | null,
 * }} opts
 */
export function buildChargesFromCheckCart({
  packagingCharges,
  deliveryCharges,
  gstPercent,
  cartTotal,
  discount,
  fulfillmentType,
  appliedCoupon,
}) {
  const packagingFee = resolvePackagingCharge(packagingCharges, appliedCoupon);
  const baseDelivery = fulfillmentType === 'pickup' ? 0 : Math.max(0, Number(deliveryCharges) || 0);
  const delivery = resolveDeliveryCharge({
    baseDelivery,
    fulfillmentType,
    appliedCoupon,
  });
  const gstTax = calculateGstAmount(gstPercent, cartTotal, packagingFee, discount);
  return { packagingFee, delivery, gstPercent, gstTax, baseDelivery };
}

/**
 * @param {number} cartTotal
 */
export function getBaseDeliveryCharge(cartTotal) {
  return cartTotal >= CART_FREE_DELIVERY_THRESHOLD || cartTotal === 0 ? 0 : CART_DELIVERY_FEE;
}

/**
 * @param {{ address1?: string, city?: string, pincode?: string }} address
 */
export function isDeliveryAddressConfirmed(address) {
  return Boolean(
    String(address?.address1 || '').trim() &&
      String(address?.city || '').trim() &&
      /^\d{6}$/.test(String(address?.pincode || '').trim()),
  );
}

/**
 * Delivery charge and GST are unknown until a delivery address is confirmed at checkout.
 * @param {string} fulfillmentType
 * @param {boolean} addressConfirmed
 */
export function shouldDeferDeliveryAndTax(fulfillmentType, addressConfirmed) {
  return fulfillmentType === 'delivery' && !addressConfirmed;
}

/**
 * @param {{
 *   cartTotal: number,
 *   discount: number,
 *   packagingFee: number,
 *   delivery: number,
 *   gstTax: number,
 *   deferDeliveryAndTax: boolean,
 * }} opts
 */
export function computeOrderGrandTotal({
  cartTotal,
  discount,
  packagingFee,
  delivery,
  gstTax,
  deferDeliveryAndTax,
}) {
  const deliveryPart = deferDeliveryAndTax ? 0 : delivery;
  const gstPart = deferDeliveryAndTax ? 0 : gstTax;
  return roundMoney(cartTotal - discount + packagingFee + deliveryPart + gstPart);
}
